
import React, { useState, useEffect } from 'react';

import {
  Eye,
  ChevronDown,
  Plus,
  Download,
  Library,
  Edit,
  Trash2,
  Share2,
  Clock,
  FileText,
  ImageIcon,
  Video,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Loader
} from "lucide-react";
import ApiService from '../../services/apiService';
import { toast } from 'react-toastify';


const syllabusData = [
  {
    subject: "Mathematics",
    progress: 65,
    color: "bg-blue-500",
    topics: [
      { name: "Numbers", progress: 100, completed: true },
      { name: "Addition & Subtraction", progress: 75 },
      { name: "Multiplication", progress: 30 },
    ],
  },
  {
    subject: "Science",
    progress: 45,
    color: "bg-green-500",
    topics: [
      { name: "Ecosystems", progress: 80 },
      { name: "Forces", progress: 30 },
      { name: "Energy", progress: 0 },
    ],
  },
  {
    subject: "Language",
    progress: 80,
    color: "bg-yellow-500",
    collapsed: true,
  },
  {
    subject: "History",
    progress: 30,
    color: "bg-red-500",
    collapsed: true,
  },
];

const LessonPlannerPage = () => {
  const [selectedSubject, setSelectedSubject] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [lessonPlans, setLessonPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSyllabus, setExpandedSyllabus] = useState({
    Mathematics: true,
    Science: true,
    Language: false,
    History: false,
  });
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [hoveredDay, setHoveredDay] = useState(null);
  const [newLessonData, setNewLessonData] = useState({
    subject: '',
    topic: '',
    grade: '',
    duration: '90'
  });
  const [showAddForm, setShowAddForm] = useState(false);

  const apiService = new ApiService();

  // Load lesson plans from shikshak-agent
  const loadLessonPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Generate week string for current date
      const weekStart = new Date(currentDate.getTime() - currentDate.getDay() * 24 * 60 * 60 * 1000);
      const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
      const weekString = `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
      
      // Call shikshak-agent lesson plan API - GET with query params
      const lessonParams = new URLSearchParams({
        week: weekString,
        grades: '3,4,5',
        subject: 'General',
        language: 'English'
      });
      const response = await apiService.makeApiCall('shikshak', `/api/lesson-plan?${lessonParams}`, null, 'GET');
      
      if (response.status === 'success' && response.data) {
        // Transform API response to lesson plan format
        const transformedPlans = [
          {
            id: 1,
            subject: "Mathematics",
            subjectColor: "bg-blue-500",
            chapter: "Numbers and Operations",
            topic: response.data.weekActivities?.[0] || "Place Value",
            time: "09:00 AM - 10:30 AM",
            objectives: response.data.objectives || "Students will understand basic mathematical concepts",
            resources: [
              { name: "Math Guide.pdf", type: "pdf", icon: FileText },
              { name: "Number Chart.png", type: "image", icon: ImageIcon },
            ],
            apiGenerated: true
          },
          {
            id: 2,
            subject: "Science",
            subjectColor: "bg-green-500",
            chapter: "Life Sciences",
            topic: response.data.weekActivities?.[1] || "Ecosystems",
            time: "11:00 AM - 12:30 PM",
            objectives: response.data.learningGoals || "Students will explore scientific concepts",
            resources: [
              { name: "Science Notes.pdf", type: "pdf", icon: FileText },
              { name: "Ecosystem Diagram.png", type: "image", icon: ImageIcon },
            ],
            apiGenerated: true
          },
          {
            id: 3,
            subject: response.data.subject || "Language",
            subjectColor: "bg-yellow-500",
            chapter: "Communication Skills",
            topic: response.data.weekActivities?.[2] || "Reading Comprehension",
            time: "02:00 PM - 03:30 PM",
            objectives: response.data.description || "Develop language and communication skills",
            resources: [],
            apiGenerated: true
          }
        ];
        
        setLessonPlans(transformedPlans);
        toast.success('Lesson plans loaded successfully!');
      } else {
        throw new Error('Failed to load lesson plans');
      }
    } catch (err) {
      console.error('Failed to load lesson plans:', err);
      setError(err.message);
      
      // Use fallback static data
      const fallbackPlans = [
        {
          id: 1,
          subject: "Mathematics",
          subjectColor: "bg-blue-500",
          chapter: "Chapter 1: Numbers",
          topic: "Place Value",
          time: "09:00 AM - 10:30 AM",
          objectives: "Students will learn to identify and understand place values in numbers up to 1000.",
          resources: [
            { name: "Place Value Guide.pdf", type: "pdf", icon: FileText },
            { name: "Number Chart.png", type: "image", icon: ImageIcon },
            { name: "Place Value Tutorial.mp4", type: "video", icon: Video },
          ],
        },
        {
          id: 2,
          subject: "Science",
          subjectColor: "bg-green-500",
          chapter: "Chapter 3: Ecosystems",
          topic: "Food Chains",
          time: "11:00 AM - 12:30 PM",
          objectives: "Students will understand the concept of food chains and their importance in ecosystems.",
          resources: [
            { name: "Ecosystem Notes.pdf", type: "pdf", icon: FileText },
            { name: "Food Chain Diagram.png", type: "image", icon: ImageIcon },
          ],
        },
        {
          id: 3,
          subject: "Language",
          subjectColor: "bg-yellow-500",
          chapter: "Chapter 3: Essay Writing",
          topic: "Learning Objectives",
          time: "02:00 PM - 03:30 PM",
          objectives: "Master the structure of formal essays and develop critical writing skills.",
          resources: [],
        }
      ];
      setLessonPlans(fallbackPlans);
      toast.warning('Using fallback lesson plans');
    } finally {
      setLoading(false);
    }
  };

  // Generate new lesson plan
  const generateNewLesson = async () => {
    if (!newLessonData.subject || !newLessonData.topic || !newLessonData.grade) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Use vidya-agent content generation for creating lesson content
      const contentResponse = await apiService.makeApiCall('vidya', '/api/generate-content', {
        concept: newLessonData.topic,
        gradeLevel: newLessonData.grade,
        language: 'English'
      }, 'POST');
      
      if (contentResponse.status === 'success') {
        const newLesson = {
          id: Date.now(),
          subject: newLessonData.subject,
          subjectColor: getSubjectColor(newLessonData.subject),
          chapter: `Grade ${newLessonData.grade}: ${newLessonData.topic}`,
          topic: newLessonData.topic,
          time: `${new Date().toLocaleTimeString()} - ${new Date(Date.now() + parseInt(newLessonData.duration) * 60000).toLocaleTimeString()}`,
          objectives: contentResponse.data.story?.substring(0, 200) || `Learn about ${newLessonData.topic}`,
          resources: [],
          apiGenerated: true,
          createdAt: new Date().toISOString()
        };
        
        setLessonPlans(prev => [newLesson, ...prev]);
        setShowAddForm(false);
        setNewLessonData({ subject: '', topic: '', grade: '', duration: '90' });
        toast.success('New lesson plan created!');
      } else {
        throw new Error('Failed to generate lesson content');
      }
    } catch (err) {
      console.error('Failed to create lesson:', err);
      toast.error(`Failed to create lesson: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getSubjectColor = (subject) => {
    const colors = {
      'Mathematics': 'bg-blue-500',
      'Science': 'bg-green-500',
      'Language': 'bg-yellow-500',
      'History': 'bg-red-500',
      'Art': 'bg-purple-500'
    };
    return colors[subject] || 'bg-gray-500';
  };

  // Load lesson plans on mount and when date changes
  useEffect(() => {
    loadLessonPlans();
  }, [currentDate]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate) === 0 ? 6 : getFirstDayOfMonth(currentDate) - 1;
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = day === selectedDay;
      const isHovered = day === hoveredDay;
      const hasLesson = [9, 15, 22, 28].includes(day); // Days with lessons

      days.push(
        <button
          key={day}
          onClick={() => setSelectedDay(day)}
          onMouseEnter={() => setHoveredDay(day)}
          onMouseLeave={() => setHoveredDay(null)}
          className={`h-8 w-8 rounded-lg text-sm font-medium transition-all duration-300 ease-out flex items-center justify-center relative ${
            isSelected
              ? "bg-blue-600 text-white shadow-lg transform scale-110"
              : isHovered
                ? "bg-blue-100 text-blue-600 transform scale-105"
                : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
          }`}
        >
          {day}
          {hasLesson && (
            <div
              className={`absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full transition-all duration-200 ${
                isSelected ? "bg-white" : "bg-blue-500"
              }`}
            ></div>
          )}
        </button>
      );
    }

    return days;
  };

  const toggleSyllabusExpansion = (subject) => {
    setExpandedSyllabus((prev) => ({
      ...prev,
      [subject]: !prev[subject],
    }));
  };

  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex gap-6">
          {/* Main Content Area */}
          <div className="flex-1 space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Loading lesson plans...</span>
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  <span>Last updated: {new Date().toLocaleTimeString()}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadLessonPlans}
              disabled={loading}
              className="text-blue-600 hover:text-blue-700 p-2 rounded transition-colors duration-200 disabled:opacity-50"
              title="Refresh Lesson Plans"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            
            <div className="relative">
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="pl-3 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none bg-white"
              >
                <option value="">Filter by subject</option>
                <option value="mathematics">Mathematics</option>
                <option value="science">Science</option>
                <option value="language">Language</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">API Error: {error}</span>
          </div>
        )}
        </div>

        {/* Date and Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white rounded-xl border border-gray-200 p-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">July 28, 2025</h1>
            <p className="text-gray-600">Saturday</p>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {showAddForm ? 'Cancel' : 'Add Lesson'}
            </button>
            <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-2">
              <Library className="w-4 h-4" />
              Import from Library
            </button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2">
              <Download className="w-4 h-4" />
              Download PDF
            </button>
          </div>

        {/* Add Lesson Form */}
        {showAddForm && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Create New Lesson Plan</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <select
                  value={newLessonData.subject}
                  onChange={(e) => setNewLessonData({...newLessonData, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Subject</option>
                  <option value="Mathematics">Mathematics</option>
                  <option value="Science">Science</option>
                  <option value="Language">Language</option>
                  <option value="History">History</option>
                  <option value="Art">Art</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                <select
                  value={newLessonData.grade}
                  onChange={(e) => setNewLessonData({...newLessonData, grade: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Grade</option>
                  <option value="1">Grade 1</option>
                  <option value="2">Grade 2</option>
                  <option value="3">Grade 3</option>
                  <option value="4">Grade 4</option>
                  <option value="5">Grade 5</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
                <input
                  type="text"
                  value={newLessonData.topic}
                  onChange={(e) => setNewLessonData({...newLessonData, topic: e.target.value})}
                  placeholder="Enter lesson topic"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                <input
                  type="number"
                  value={newLessonData.duration}
                  onChange={(e) => setNewLessonData({...newLessonData, duration: e.target.value})}
                  placeholder="90"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={generateNewLesson}
                disabled={loading}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Generate Lesson
              </button>
            </div>
          </div>
        )}
        </div>

        {/* Lesson Plans */}
        <div className="space-y-6">
          {lessonPlans.map((lesson) => (
            <div
              key={lesson.id}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow duration-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 ${lesson.subjectColor} text-white text-sm rounded-full font-medium`}>
                    {lesson.subject}
                  </span>
                  <div className="flex items-center gap-2 text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{lesson.time}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200">
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{lesson.chapter}</h3>
                <p className="text-gray-600">{lesson.topic}</p>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Learning Objectives</h4>
                <p className="text-gray-600 text-sm">{lesson.objectives}</p>
              </div>

              {lesson.resources.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Resources</h4>
                  <div className="flex flex-wrap gap-3">
                    {lesson.resources.map((resource, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                      >
                        <resource.icon className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">{resource.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

          {/* Right Sidebar */}
          <div className="w-80 flex-shrink-0 space-y-6 sticky top-6 self-start">
            {/* Syllabus Overview */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-6 text-lg">Syllabus Overview</h3>

              <div className="space-y-4">
                {syllabusData.map((item, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 ${item.color} rounded-full`}></div>
                        <span className="font-medium text-gray-900">{item.subject}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">{item.progress}%</span>
                        {!item.collapsed && (
                          <button
                            onClick={() => toggleSyllabusExpansion(item.subject)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
                          >
                            {expandedSyllabus[item.subject] ? (
                              <ChevronUp className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`${item.color} h-2 rounded-full transition-all duration-500 ease-out`}
                          style={{ width: `${item.progress}%` }}
                        ></div>
                      </div>
                    </div>

                    {item.topics && expandedSyllabus[item.subject] && (
                      <div className="ml-6 space-y-2">
                        {item.topics.map((topic, topicIndex) => (
                          <div key={topicIndex} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-2 h-2 rounded-full ${topic.completed ? "bg-green-500" : "bg-gray-300"}`}
                              ></div>
                              <span className="text-sm text-gray-700">{topic.name}</span>
                            </div>
                            <span className="text-xs text-gray-500">{topic.progress}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Calendar */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">February 2024</h3>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="p-1.5 hover:bg-gray-100 rounded transition-all duration-200 hover:scale-110"
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => navigateMonth(1)}
                    className="p-1.5 hover:bg-gray-100 rounded transition-all duration-200 hover:scale-110"
                  >
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonPlannerPage;